// src/context/UserContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

// Create the context
const UserContext = createContext();

// Create a custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [aiSearchesUsed, setAiSearchesUsed] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch current user from API (skip if not authenticated)
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');

      // If there's no token, avoid calling the protected endpoint to prevent 401 noise
      if (!token) {
        console.debug('[UserContext] No auth token found; skipping /users/me request');
        setUser(null);
        setAiSearchesUsed(0);
        return;
      }

      const res = await api.get('/v1/users/me');
      const current = res.data?.data?.user;
      setUser(current || null);
      setAiSearchesUsed(current?.aiSearchesUsed || 0);
    } catch (err) {
      setUser(null);
      setAiSearchesUsed(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const incrementAISearch = () => {
    if (user && user.plan === 'free') {
      setAiSearchesUsed(prev => {
        const newValue = prev + 1;
        setUser(prevUser => ({ ...prevUser, aiSearchesUsed: newValue }));
        return newValue;
      });
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const upgradeToPro = () => {
    // Redirect to checkout page instead of just updating local state
    window.location.href = '/checkout';
  };

  const downgradeToFree = () => {
    setUser(prev => ({ 
      ...prev, 
      plan: 'free',
      aiSearchLimit: 5 
    }));
  };

  const userType = user?.plan; // 'free' or 'pro'

  const value = {
    user,
    userType,
    aiSearchesUsed,
    loading,
    refreshUser,
    incrementAISearch,
    updateUser,
    upgradeToPro,
    downgradeToFree,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Export the context itself
export { UserContext };